import { useQuery } from '@apollo/client';
import { AlertTriangle, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PRODUCTS_QUERY } from '@/modules/catalog/products/products.gql';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { useAuthStore } from '@/stores/authStore';

import {
  EXPIRED_BATCHES_QUERY,
  EXPIRING_BATCHES_QUERY,
  LOW_STOCK_PRODUCTS_QUERY,
} from './inventory.gql';
import { MovementFormDialog } from './MovementFormDialog';
import { ProductBatchesDialog } from './ProductBatchesDialog';

interface Product {
  id: string;
  code: string;
  name: string;
  stock_qty: string;
  stock_min: string;
  status: 'active' | 'inactive' | 'discontinued';
  branch_id: string | null;
  is_controlled: boolean;
  is_batch_tracked: boolean;
  is_perishable: boolean;
}

interface ProductsResponse {
  products: {
    items: Product[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

interface LowStockResponse {
  lowStockProducts: Pick<Product, 'id' | 'code' | 'name' | 'stock_qty' | 'stock_min' | 'branch_id'>[];
}

interface ExpiryBatch {
  id: string;
  product_name: string;
  lot_number: string;
  expiration_date: string;
  quantity_remaining: string;
}

interface ExpiringResponse {
  expiringBatches: ExpiryBatch[];
}

interface ExpiredResponse {
  expiredBatches: ExpiryBatch[];
}

export function InventoryPage(): JSX.Element {
  const { t } = useTranslation();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_write = user?.permissions.includes('inventory.write') ?? false;

  const [low_stock_only, setLowStockOnly] = useState(false);
  const [register_for, setRegisterFor] = useState<{
    id: string;
    code: string;
    name: string;
    is_batch_tracked: boolean;
    is_perishable: boolean;
  } | null>(null);
  const [register_open, setRegisterOpen] = useState(false);
  const [batches_for, setBatchesFor] = useState<{ id: string; name: string } | null>(null);
  const [batches_open, setBatchesOpen] = useState(false);

  const { data, loading, refetch } = useQuery<ProductsResponse>(PRODUCTS_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
      search: list.search || null,
      status_filter: list.status_filter,
      low_stock_only: low_stock_only || null,
    },
  });
  const { data: low_data, refetch: refetchLow } = useQuery<LowStockResponse>(
    LOW_STOCK_PRODUCTS_QUERY,
    { variables: { limit: 5 } },
  );
  const { data: expiring_data } = useQuery<ExpiringResponse>(EXPIRING_BATCHES_QUERY, {
    variables: { limit: 10 },
  });
  const { data: expired_data } = useQuery<ExpiredResponse>(EXPIRED_BATCHES_QUERY, {
    variables: { limit: 10 },
  });

  const expiring_batches = expiring_data?.expiringBatches ?? [];
  const expired_batches = expired_data?.expiredBatches ?? [];
  const has_expiry_alerts = expiring_batches.length > 0 || expired_batches.length > 0;

  // Pick a default branch_id for the movement dialog: use the first product's branch
  // or fall back to the user's branch (added in stage 9 if absent now). Stage 6
  // assumes a single branch — Stage 13 wires a proper branch picker.
  const default_branch_id =
    data?.products.items.find((p) => p.branch_id)?.branch_id ??
    low_data?.lowStockProducts.find((p) => p.branch_id)?.branch_id ??
    '';

  const onClickRegisterFor = (p: Product): void => {
    setRegisterFor({
      id: p.id,
      code: p.code,
      name: p.name,
      is_batch_tracked: p.is_batch_tracked,
      is_perishable: p.is_perishable,
    });
    setRegisterOpen(true);
  };

  const onClickRegisterBlank = (): void => {
    setRegisterFor(null);
    setRegisterOpen(true);
  };

  const onClickViewBatches = (p: Product): void => {
    setBatchesFor({ id: p.id, name: p.name });
    setBatchesOpen(true);
  };

  const columns: CrudColumn<Product>[] = [
    {
      key: 'code',
      header_key: 'catalog.products.code',
      width: 'w-32',
      render: (r) => <span className="font-mono text-xs">{r.code}</span>,
    },
    { key: 'name', header_key: 'catalog.products.name', render: (r) => r.name },
    {
      key: 'stock',
      header_key: 'catalog.products.stock',
      align: 'right',
      width: 'w-32',
      render: (r) => {
        const qty = Number(r.stock_qty);
        const min = Number(r.stock_min);
        const low = !Number.isNaN(qty) && !Number.isNaN(min) && qty <= min;
        return (
          <span className={low ? 'font-semibold text-destructive' : undefined}>{r.stock_qty}</span>
        );
      },
    },
    {
      key: 'min',
      header_key: 'inventory.stock_min',
      align: 'right',
      width: 'w-24',
      render: (r) => r.stock_min,
    },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-32',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-64',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onClickViewBatches(r)}
          >
            {t('inventory.view_batches')}
          </Button>
          {can_write ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onClickRegisterFor(r)}
            >
              {t('inventory.register_short')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">{t('inventory.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('inventory.subtitle')}</p>
      </header>

      {(low_data?.lowStockProducts.length ?? 0) > 0 ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader className="flex flex-row items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden />
            <CardTitle className="text-base text-destructive">{t('inventory.low_stock_alert')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {low_data?.lowStockProducts.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between">
                <span>
                  <span className="font-mono text-xs">{p.code}</span> — {p.name}
                </span>
                <span className="font-semibold text-destructive">
                  {p.stock_qty} ≤ {p.stock_min}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {has_expiry_alerts ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-amber-500/40 bg-amber-500/5">
            <CardHeader className="flex flex-row items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
              <CardTitle className="text-base text-amber-700">{t('inventory.expiring_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              {expiring_batches.length === 0 ? (
                <p className="text-muted-foreground">{t('inventory.no_expiring')}</p>
              ) : (
                expiring_batches.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-2">
                    <span>
                      {b.product_name} · <span className="font-mono text-xs">{b.lot_number}</span> ·{' '}
                      {b.expiration_date}
                    </span>
                    <span className="font-semibold text-amber-700">{b.quantity_remaining}</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="border-destructive/40 bg-destructive/5">
            <CardHeader className="flex flex-row items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden />
              <CardTitle className="text-base text-destructive">{t('inventory.expired_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              {expired_batches.length === 0 ? (
                <p className="text-muted-foreground">{t('inventory.no_expired')}</p>
              ) : (
                expired_batches.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-2">
                    <span>
                      {b.product_name} · <span className="font-mono text-xs">{b.lot_number}</span> ·{' '}
                      {b.expiration_date}
                    </span>
                    <span className="font-semibold text-destructive">{b.quantity_remaining}</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}

      <CrudTable<Product>
        rows={data?.products.items ?? []}
        columns={columns}
        loading={loading}
        page={data?.products.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="catalog.products.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            filters={
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={low_stock_only}
                  onChange={(e) => setLowStockOnly(e.target.checked)}
                />
                {t('catalog.products.low_stock_filter')}
              </label>
            }
            primary_action={
              can_write ? (
                <Button type="button" onClick={onClickRegisterBlank}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('inventory.register_movement')}</span>
                </Button>
              ) : undefined
            }
          />
        }
      />

      <p className="text-xs text-muted-foreground">
        {t('inventory.movements_link_hint')}{' '}
        <Link to="/app/inventory/movements" className="text-primary hover:underline">
          {t('inventory.movements_link')}
        </Link>
      </p>

      {register_open && default_branch_id ? (
        <MovementFormDialog
          branch_id={default_branch_id}
          initial_product={register_for ?? undefined}
          onClose={() => setRegisterOpen(false)}
          onSaved={async () => {
            setRegisterOpen(false);
            await refetch();
            await refetchLow();
          }}
        />
      ) : null}

      {batches_for ? (
        <ProductBatchesDialog
          product_id={batches_for.id}
          product_name={batches_for.name}
          open={batches_open}
          on_close={() => setBatchesOpen(false)}
        />
      ) : null}
    </div>
  );
}
